import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import AccountInfoStep from './AccountInfoStep'
import FileUploadStep from './FileUploadStep'
import ColumnMappingStep from './ColumnMappingStep'
import DataCleaningStep from './DataCleaningStep'
import ReviewConfirmStep from './ReviewConfirmStep'

function ImportOperationsWizard({
  open,
  onClose,
  accounts,
  onImportComplete,
  initialStep = 0,
  initialAccount = null,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const steps = useMemo(
    () => [
      t('mesComptes.import.steps.accountInfo'),
      t('mesComptes.import.steps.uploadFile'),
      t('mesComptes.import.steps.mapColumns'),
      t('mesComptes.import.steps.cleanData'),
      t('mesComptes.import.steps.reviewConfirm'),
    ],
    [t]
  ) // LAP-733 (GE022)
  const [activeStep, setActiveStep] = useState(initialStep)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Step 1 data (Account Information)
  const [accountInfo, setAccountInfo] = useState({
    bankData: null,
    accountType: null,
    IBAN: '',
    NumeroCompte: '',
    Devise: '',
    DateOuverture: '',
    NumeroClient: '',
    CléRiB: '',
  })

  // Step 2 data (File Upload)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [fileData, setFileData] = useState(null)

  // Step 3 data (Column Mapping)
  const [columnMapping, setColumnMapping] = useState({})
  const [mappingTemplates, setMappingTemplates] = useState([])
  // 'single' (une colonne Montant) ou 'credit_debit' (deux colonnes signées)
  const [amountMode, setAmountMode] = useState('single')

  // Step 4 data (Data Cleaning)
  const [signLogic, setSignLogic] = useState('already_signed')
  const [signColumn, setSignColumn] = useState('')

  // Step 5 data (Preview)
  const [previewData, setPreviewData] = useState(null)

  useEffect(() => {
    if (open) {
      // Reset state when dialog opens
      setActiveStep(initialStep)
      setError(null)
      if (initialAccount) {
        setAccountInfo({
          bankData: initialAccount.Nom || null,
          accountType: initialAccount.TypeCompte || null,
          IBAN: initialAccount.IBAN || '',
          NumeroCompte: initialAccount.NumeroCompte || '',
          Devise: initialAccount.Devise || '',
          DateOuverture: initialAccount.DateOuverture || '',
          NumeroClient: initialAccount.NumeroClient || '',
          CléRiB: initialAccount.CléRiB || '',
          id: initialAccount.id,
        })
      } else {
        setAccountInfo({
          bankData: null,
          accountType: null,
          IBAN: '',
          NumeroCompte: '',
          Devise: '',
          DateOuverture: '',
          NumeroClient: '',
          CléRiB: '',
        })
      }
      setUploadedFile(null)
      setFileData(null)
      setColumnMapping({})
      setAmountMode('single')
      setSignLogic('already_signed')
      setSignColumn('')
      setPreviewData(null)
    }
  }, [open, initialStep, initialAccount])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    if (activeStep === initialStep && initialStep > 0) {
      return
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(initialStep)
    setError(null)
    if (initialAccount) {
      setAccountInfo({
        bankData: initialAccount.Nom || null,
        accountType: initialAccount.TypeCompte || null,
        IBAN: initialAccount.IBAN || '',
        NumeroCompte: initialAccount.NumeroCompte || '',
        Devise: initialAccount.Devise || '',
        DateOuverture: initialAccount.DateOuverture || '',
        NumeroClient: initialAccount.NumeroClient || '',
        CléRiB: initialAccount.CléRiB || '',
        id: initialAccount.id,
      })
    } else {
      setAccountInfo({
        bankData: null,
        accountType: null,
        IBAN: '',
        NumeroCompte: '',
        Devise: '',
        DateOuverture: '',
        NumeroClient: '',
        CléRiB: '',
      })
    }
    setUploadedFile(null)
    setFileData(null)
    setColumnMapping({})
    setAmountMode('single')
    setSignLogic('already_signed')
    setSignColumn('')
    setPreviewData(null)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AccountInfoStep
            accountInfo={accountInfo}
            setAccountInfo={setAccountInfo}
            setError={setError}
            onNext={handleNext}
          />
        )
      case 1:
        return (
          <FileUploadStep
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            fileData={fileData}
            setFileData={setFileData}
            setError={setError}
            setLoading={setLoading}
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case 2:
        return (
          <ColumnMappingStep
            fileData={fileData}
            columnMapping={columnMapping}
            setColumnMapping={setColumnMapping}
            amountMode={amountMode}
            setAmountMode={setAmountMode}
            accountInfo={accountInfo}
            mappingTemplates={mappingTemplates}
            setMappingTemplates={setMappingTemplates}
            setError={setError}
            setLoading={setLoading}
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case 3:
        return (
          <DataCleaningStep
            amountMode={amountMode}
            signLogic={signLogic}
            setSignLogic={setSignLogic}
            signColumn={signColumn}
            setSignColumn={setSignColumn}
            fileData={fileData}
            columnMapping={columnMapping}
            accountInfo={accountInfo}
            setPreviewData={setPreviewData}
            setError={setError}
            setLoading={setLoading}
            onNext={handleNext}
            onBack={handleBack}
          />
        )
      case 4:
        return (
          <ReviewConfirmStep
            previewData={previewData}
            uploadedFile={uploadedFile}
            fileData={fileData}
            accountInfo={accountInfo}
            columnMapping={columnMapping}
            setError={setError}
            setLoading={setLoading}
            onBack={handleBack}
            onComplete={() => {
              onImportComplete()
              handleClose()
            }}
          />
        )
      default:
        return t('mesComptes.import.unknownStep') /* LAP-733 (GE022) */
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          {t('mesComptes.import.title') /* LAP-733 (GE022) */}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ width: '100%', minHeight: 500 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>{getStepContent(activeStep)}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {t('mesComptes.import.cancel') /* LAP-733 (GE022) */}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportOperationsWizard
